// filename: src/impl/onyx-core.ts
import type { ResolvedConfig } from '../config/types';
import { DEFAULT_AI_MODEL } from '../config/defaults';
import { HttpClient, parseJsonAllowNaN } from '../core/http';
import { openJsonLinesStream, openMessagePackStream } from '../core/stream';
import { encodeMessagePack, MESSAGEPACK_ACCEPT, MESSAGEPACK_MEDIA_TYPE } from '../core/msgpack';

import type {
  OnyxFacade,
  IOnyxDatabase,
  OnyxConfig,
  WireFormat,
  AiChatOptions,
  AiClient,
  AiChatClient,
  AiChatCompletionRequest,
  AiChatCompletionResponse,
  AiChatCompletionChunk,
  AiChatCompletionStream,
  AiModelsResponse,
  AiModel,
  AiScriptApprovalRequest,
  AiScriptApprovalResponse,
  AiRequestOptions,
  FullTextSearchResult,
  PublishedModelPredictionInputs,
  PublishedModelPredictionResponse,
} from '../types/public';
import type {
  ConditionInput,
  IQueryBuilder,
  ISaveBuilder,
  ICascadeBuilder,
  IConditionBuilder,
  ICascadeRelationshipBuilder,
} from '../types/builders';
import { QueryResults, QueryResultsPromise } from '../builders/query-results';
import type {
  QueryCriteria,
  QueryCondition,
  SelectQuery,
  UpdateQuery,
  QueryPage,
} from '../types/protocol';
import type {
  ApproximateSearchOptions,
  HnswSearchQueryInput,
  SearchOptions,
  Sort,
  StreamAction,
  OnyxDocument,
  FetchImpl,
  VectorSearchQueryInput,
} from '../types/common';
import { normalizeCondition } from '../helpers/condition-normalizer';
import {
  assertCandidateConditionIsReadOnly,
  assertCandidateConditionIsComposable,
  assertSearchConditionIsComposable,
  assertSearchConditionSupportsStreaming,
} from '../helpers/candidate-condition';
import {
  approximateIndexCandidateQuery,
  hnswSearchQuery,
  vectorSearchQuery,
} from '../helpers/candidate-search';
import { searchCriteriaValue } from '../helpers/search-options';
import type {
  SchemaDiff,
  SchemaEntity,
  SchemaHistoryEntry,
  SchemaRevision,
  SchemaUpsertRequest,
  SchemaValidationResult,
  SecretMetadata,
  SecretRecord,
  SecretsListResponse,
  SecretSaveRequest,
} from '../types/public';
import { CascadeRelationshipBuilder } from '../builders/cascade-relationship-builder';
import { OnyxHttpError } from '../errors/http-error';
import { computeSchemaDiff } from '../helpers/schema-diff';
import {
  collectAllQueryRecords,
  formatQueryResultsAsCsv,
  formatQueryResultsAsJson,
  formatQueryResultsAsTable,
  formatQueryResultsAsTree,
} from '../helpers/query-formatters';
import type {
  CsvFormatOptions,
  JsonFormatOptions,
  TableFormatOptions,
  TreeFormatOptions,
} from '../types/formatters';

export type ResolveConfig = (config?: OnyxConfig) => Promise<ResolvedConfig>;

const DEFAULT_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

/** -------------------------
 * Internal helpers
 * --------------------------*/
function toSingleCondition(criteria: QueryCriteria): QueryCondition {
  return { conditionType: 'SingleCondition', criteria };
}

function flattenStrings(values: Array<string | string[]>): string[] {
  const flat: string[] = [];
  for (const value of values) {
    if (Array.isArray(value)) {
      flat.push(...value);
    } else if (typeof value === 'string') {
      flat.push(value);
    }
  }
  return flat;
}

function toCondition(input: ConditionInput): QueryCondition {
  if (typeof (input as IConditionBuilder).toCondition === 'function') {
    return (input as IConditionBuilder).toCondition();
  }
  if (
    input &&
    ((input as QueryCondition).conditionType === 'SingleCondition' ||
      (input as QueryCondition).conditionType === 'CompoundCondition')
  ) {
    return input as QueryCondition;
  }
  const c = input as QueryCriteria;
  if (c && typeof c.field === 'string' && typeof c.operator === 'string') {
    return toSingleCondition(c);
  }
  throw new Error('Invalid condition passed to builder.');
}

type SecretMetadataPayload = Omit<SecretMetadata, 'updatedAt'> & { updatedAt: string | Date };
type SecretRecordPayload = Omit<SecretRecord, 'updatedAt'> & { updatedAt: string | Date };
type SchemaRevisionPayload = Omit<SchemaRevision, 'meta'> & {
  meta?: {
    revisionId?: string;
    createdAt?: string | Date;
    publishedAt?: string | Date;
  };
  createdAt?: string | Date;
  publishedAt?: string | Date;
  revisionId?: string;
};
type SchemaRevisionPayloadWithEntityText = SchemaRevisionPayload & { entityText?: unknown };
type SchemaValidationPayload = SchemaValidationResult & { schema?: SchemaRevisionPayload };
type SchemaValidationPayloadWithEntityText = SchemaValidationPayload & {
  schema?: SchemaRevisionPayloadWithEntityText;
};

function serializeDates(value: unknown): unknown {
  if (value instanceof Date) return value.toISOString();
  if (Array.isArray(value)) return value.map(serializeDates);
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = serializeDates(v);
    }
    return out;
  }
  return value;
}

function stripEntityText<T extends Record<string, unknown>>(input: T): Omit<T, 'entityText'> {
  const { entityText, ...rest } = input;
  void entityText;
  return rest;
}

function normalizeSecretMetadata(input: SecretMetadataPayload): SecretMetadata {
  return { ...input, updatedAt: new Date(input.updatedAt) };
}

function normalizeSecretRecord(input: SecretRecordPayload): SecretRecord {
  return { ...input, updatedAt: new Date(input.updatedAt) };
}

function normalizeDate(value?: string | Date): Date | undefined {
  if (value == null) return undefined;
  if (value instanceof Date) return value;
  const ts = new Date(value);
  return Number.isNaN(ts.getTime()) ? undefined : ts;
}

function normalizeSchemaRevision(
  input: SchemaRevisionPayloadWithEntityText,
  fallbackDatabaseId: string,
): SchemaRevision {
  const {
    meta,
    createdAt,
    publishedAt,
    revisionId,
    entityText,
    databaseId,
    entities,
    revisionDescription,
    ...rest
  } = input;
  void entityText;
  const dbId = typeof databaseId === 'string' ? databaseId : fallbackDatabaseId;
  const entityList = Array.isArray(entities) ? (entities as SchemaEntity[]) : [];
  const revisionDesc = typeof revisionDescription === 'string' ? revisionDescription : undefined;
  const mergedMeta = {
    revisionId: meta?.revisionId ?? revisionId,
    createdAt: normalizeDate(meta?.createdAt ?? createdAt),
    publishedAt: normalizeDate(meta?.publishedAt ?? publishedAt),
  };
  const cleanedMeta =
    mergedMeta.revisionId || mergedMeta.createdAt || mergedMeta.publishedAt ? mergedMeta : undefined;
  return {
    databaseId: dbId,
    revisionDescription: revisionDesc,
    entities: entityList,
    meta: cleanedMeta,
    ...rest,
  };
}

/** -------------------------
 * Onyx Database Implementation
 * --------------------------*/
class OnyxDatabaseImpl<Schema = Record<string, unknown>> implements IOnyxDatabase<Schema> {
  private readonly cfgPromise: Promise<ResolvedConfig>;
  private resolved: ResolvedConfig | null = null;
  private http: HttpClient | null = null;
  private aiHttp: HttpClient | null = null;
  private readonly streams = new Set<{ cancel: () => void }>();
  private readonly requestLoggingEnabled: boolean;
  private readonly responseLoggingEnabled: boolean;
  private readonly defaultPartition: string | undefined;
  readonly ai: AiClient;

  constructor(
    config: OnyxConfig | undefined,
    resolveConfigWithCache: (config?: OnyxConfig) => Promise<ResolvedConfig>,
  ) {
    // Defer resolution; keeps init() synchronous
    this.requestLoggingEnabled = !!config?.requestLoggingEnabled;
    this.responseLoggingEnabled = !!config?.responseLoggingEnabled;
    this.defaultPartition = config?.partition;
    this.cfgPromise = resolveConfigWithCache(config);
    this.ai = this.createAiFacade();
  }

  private async resolveConfig(): Promise<ResolvedConfig> {
    if (!this.resolved) {
      this.resolved = await this.cfgPromise;
    }
    return this.resolved;
  }

  private async ensureClient(): Promise<{
    http: HttpClient;
    fetchImpl: FetchImpl;
    baseUrl: string;
    databaseId: string;
    defaultModel: string;
    wireFormat: WireFormat;
  }> {
    const cfg = await this.resolveConfig();
    if (!this.http) {
      this.http = new HttpClient({
        baseUrl: cfg.baseUrl,
        apiKey: cfg.apiKey,
        apiSecret: cfg.apiSecret,
        fetchImpl: cfg.fetch,
        requestLoggingEnabled: this.requestLoggingEnabled,
        responseLoggingEnabled: this.responseLoggingEnabled,
        retryEnabled: cfg.retryEnabled,
        maxRetries: cfg.maxRetries,
        retryInitialDelayMs: cfg.retryInitialDelayMs,
        wireFormat: cfg.wireFormat,
      });
    }
    return {
      http: this.http,
      fetchImpl: cfg.fetch,
      baseUrl: cfg.baseUrl,
      databaseId: cfg.databaseId,
      defaultModel: cfg.defaultModel ?? DEFAULT_AI_MODEL,
      wireFormat: cfg.wireFormat,
    };
  }

  private async ensureAiClient(): Promise<{
    http: HttpClient;
    fetchImpl: FetchImpl;
    aiBaseUrl: string;
    databaseId: string;
    defaultModel: string;
  }> {
    const cfg = await this.resolveConfig();
    if (!this.aiHttp) {
      this.aiHttp = new HttpClient({
        baseUrl: cfg.aiBaseUrl,
        apiKey: cfg.apiKey,
        apiSecret: cfg.apiSecret,
        fetchImpl: cfg.fetch,
        requestLoggingEnabled: this.requestLoggingEnabled,
        responseLoggingEnabled: this.responseLoggingEnabled,
        retryEnabled: cfg.retryEnabled,
        maxRetries: cfg.maxRetries,
        retryInitialDelayMs: cfg.retryInitialDelayMs,
      });
    }
    return {
      http: this.aiHttp,
      fetchImpl: cfg.fetch,
      aiBaseUrl: cfg.aiBaseUrl,
      databaseId: cfg.databaseId,
      defaultModel: cfg.defaultModel ?? DEFAULT_AI_MODEL,
    };
  }

  private registerStream(handle: { cancel: () => void }): { cancel: () => void } {
    this.streams.add(handle);
    return {
      cancel: () => {
        try {
          handle.cancel();
        } finally {
          this.streams.delete(handle);
        }
      },
    };
  }

  private createAiFacade(): AiClient {
    const chat = ((contentOrRequest: string | AiChatCompletionRequest, options?: AiChatOptions) => {
      if (typeof contentOrRequest === 'string') {
        return this.chatWithContent(contentOrRequest, options);
      }
      return this.getAiChatClient().create(contentOrRequest, options);
    }) as AiClient['chat'];
    return {
      chat,
      chatClient: () => this.getAiChatClient(),
      getModels: () => this.getModels(),
      getModel: (modelId) => this.getModel(modelId),
      requestScriptApproval: (input) => this.requestScriptApproval(input),
    };
  }

  getRequestLoggingEnabled(): boolean {
    return this.requestLoggingEnabled;
  }

  getResponseLoggingEnabled(): boolean {
    return this.responseLoggingEnabled;
  }

  /** -------- IOnyxDatabase -------- */

  private getAiChatClient(): AiChatClient {
    return new AiChatClientImpl(
      () => this.ensureAiClient(),
      (handle) => this.registerStream(handle),
      () => this.requestLoggingEnabled,
      () => this.responseLoggingEnabled,
    );
  }

  private async chatWithContent(
    content: string,
    options?: AiChatOptions,
  ): Promise<string | AiChatCompletionResponse | AiChatCompletionStream> {
    const { defaultModel } = await this.ensureAiClient();
    const stream = options?.stream ?? false;
    const request: AiChatCompletionRequest = {
      model: options?.model ?? defaultModel,
      messages: [{ role: options?.role ?? 'user', content }],
      stream,
    };
    if (options && 'temperature' in options) {
      request.temperature = options.temperature ?? null;
    }
    const result = await this.getAiChatClient().create(request, options);
    if (stream) return result;
    if (options?.raw) return result;
    const first = (result as AiChatCompletionResponse).choices?.[0]?.message?.content;
    if (typeof first === 'string' && first.trim().length > 0) {
      return first;
    }
    throw new Error('Chat completion response is missing message content');
  }

  chat(
    content: string,
    options?: AiChatOptions & { stream?: false; raw?: false | undefined },
  ): Promise<string>;
  chat(
    content: string,
    options: AiChatOptions & { stream: true },
  ): Promise<AiChatCompletionStream>;
  chat(
    content: string,
    options: AiChatOptions & { raw: true },
  ): Promise<AiChatCompletionResponse | AiChatCompletionStream>;
  chat(): AiChatClient;
  chat(
    content?: string,
    options?: AiChatOptions,
  ): AiChatClient | Promise<string | AiChatCompletionResponse | AiChatCompletionStream> {
    if (typeof content === 'string') {
      return this.chatWithContent(content, options);
    }
    return this.getAiChatClient();
  }

  async getModels(): Promise<AiModelsResponse> {
    const { http } = await this.ensureAiClient();
    return http.request<AiModelsResponse>('GET', '/v1/models');
  }

  async getModel(modelId: string): Promise<AiModel> {
    const { http } = await this.ensureAiClient();
    const path = `/v1/models/${encodeURIComponent(modelId)}`;
    return http.request<AiModel>('GET', path);
  }

  async requestScriptApproval(input: AiScriptApprovalRequest): Promise<AiScriptApprovalResponse> {
    const { http } = await this.ensureAiClient();
    return http.request<AiScriptApprovalResponse>('POST', '/api/script-approvals', input);
  }

  async predict(
    publishedModelId: string,
    inputs: PublishedModelPredictionInputs,
  ): Promise<PublishedModelPredictionResponse> {
    const { http, databaseId } = await this.ensureClient();
    const path = `/data/${encodeURIComponent(
      databaseId,
    )}/model-builder/published-model/${encodeURIComponent(publishedModelId)}/predict`;
    return http.request<PublishedModelPredictionResponse>(
      'POST',
      path,
      serializeDates({ inputs }),
    );
  }

  async predictFromScript(
    publishedModelId: string,
    scriptId: string,
    scriptParameters: Record<string, string> = {},
  ): Promise<PublishedModelPredictionResponse> {
    const { http, databaseId } = await this.ensureClient();
    const path = `/data/${encodeURIComponent(
      databaseId,
    )}/model-builder/published-model/${encodeURIComponent(publishedModelId)}/predict/script`;
    return http.request<PublishedModelPredictionResponse>('POST', path, {
      scriptId,
      scriptParameters,
    });
  }

  from<Table extends keyof Schema & string>(table: Table): IQueryBuilder<Schema[Table]> {
    return new QueryBuilderImpl<Schema[Table], Schema>(this, String(table), this.defaultPartition);
  }

  select(...fields: Array<string | string[]>): IQueryBuilder<Record<string, unknown>> {
    const qb = new QueryBuilderImpl<Record<string, unknown>, Schema>(
      this,
      null,
      this.defaultPartition,
    );
    qb.select(...fields);
    return qb;
  }

  search(
    queryText: string,
    minScore?: number | null,
  ): IQueryBuilder<Record<string, unknown>>;
  search(
    queryText: string,
    options: SearchOptions,
  ): IQueryBuilder<FullTextSearchResult>;
  search(
    queryText: string,
    minScoreOrOptions?: number | null | SearchOptions,
  ): IQueryBuilder<any> {
    if (typeof queryText !== 'string') {
      throw new TypeError('Database-wide search requires text as its first argument');
    }
    const qb = new QueryBuilderImpl<Record<string, unknown>, Schema>(
      this,
      'ALL',
      undefined,
    );
    return typeof minScoreOrOptions === 'object' && minScoreOrOptions !== null
      ? qb.search(queryText, minScoreOrOptions)
      : qb.search(queryText, minScoreOrOptions);
  }

  cascade(...relationships: Array<string | string[]>): ICascadeBuilder<Schema> {
    const cb = new CascadeBuilderImpl<Schema>(this);
    return cb.cascade(...relationships);
  }

  cascadeBuilder(): ICascadeRelationshipBuilder {
    return new CascadeRelationshipBuilder();
  }

  // Overload: builder form
  save<Table extends keyof Schema & string>(table: Table): ISaveBuilder<Schema[Table]>;
  // Overload: direct form
  save<Table extends keyof Schema & string>(
    table: Table,
    entityOrEntities: Partial<Schema[Table]> | Array<Partial<Schema[Table]>>,
    options?: { relationships?: string[] },
  ): Promise<unknown>;
  // Impl
  save(
    table: string,
    entityOrEntities?: unknown,
    options?: { relationships?: string[] },
  ): ISaveBuilder<unknown> | Promise<unknown> {
    if (arguments.length === 1) {
      return new SaveBuilderImpl<unknown, Schema>(this, table);
    }
    return this._saveInternal(table, entityOrEntities as unknown, options);
  }

  async batchSave<Table extends keyof Schema & string>(
    table: Table,
    entities: Array<Partial<Schema[Table]>>,
    batchSize = 1000,
    options?: { relationships?: string[] },
  ): Promise<void> {
    for (let i = 0; i < entities.length; i += batchSize) {
      const chunk = entities.slice(i, i + batchSize);
      if (chunk.length) {
        await this._saveInternal(String(table), chunk, options);
      }
    }
  }

  async findById<Table extends keyof Schema & string, T = Schema[Table]>(
    table: Table,
    primaryKey: string,
    options?: { partition?: string; resolvers?: string[] },
  ): Promise<T | null> {
    const { http, databaseId } = await this.ensureClient();
    const params = new URLSearchParams();
    const partition = options?.partition ?? this.defaultPartition;
    if (partition) params.append('partition', partition);
    if (options?.resolvers?.length) params.append('resolvers', options.resolvers.join(','));

    const path = `/data/${encodeURIComponent(databaseId)}/${encodeURIComponent(
      String(table),
    )}/${encodeURIComponent(primaryKey)}${params.toString() ? `?${params.toString()}` : ''}`;
    try {
      return await http.requestEntity<T>('GET', path);
    } catch (err) {
      if (err instanceof OnyxHttpError && err.status === 404) return null;
      throw err;
    }
  }

  async delete<Table extends keyof Schema & string>(
    table: Table,
    primaryKey: string,
    options?: { partition?: string; relationships?: string[] },
  ): Promise<boolean> {
    const { http, databaseId } = await this.ensureClient();
    const params = new URLSearchParams();
    const partition = options?.partition ?? this.defaultPartition;
    if (partition) params.append('partition', partition);
    if (options?.relationships?.length) {
      params.append('relationships', options.relationships.map(encodeURIComponent).join(','));
    }
    const path = `/data/${encodeURIComponent(databaseId)}/${encodeURIComponent(
      table,
    )}/${encodeURIComponent(primaryKey)}${params.toString() ? `?${params.toString()}` : ''}`;
    await http.requestEntity<unknown>('DELETE', path);
    return true;
  }

  async saveDocument(doc: OnyxDocument): Promise<unknown> {
    const { http, databaseId } = await this.ensureClient();
    const path = `/data/${encodeURIComponent(databaseId)}/document`;
    return http.request('PUT', path, serializeDates(doc));
  }

  async getDocument(
    documentId: string,
    options?: { width?: number; height?: number },
  ): Promise<unknown> {
    const { http, databaseId } = await this.ensureClient();
    const params = new URLSearchParams();
    if (options?.width != null) params.append('width', String(options.width));
    if (options?.height != null) params.append('height', String(options.height));
    const path = `/data/${encodeURIComponent(databaseId)}/document/${encodeURIComponent(
      documentId,
    )}${params.toString() ? `?${params.toString()}` : ''}`;
    return http.request('GET', path);
  }

  async deleteDocument(documentId: string): Promise<unknown> {
    const { http, databaseId } = await this.ensureClient();
    const path = `/data/${encodeURIComponent(databaseId)}/document/${encodeURIComponent(
      documentId,
    )}`;
    return http.request('DELETE', path);
  }

  async getSchema(options?: { tables?: string | string[] }): Promise<SchemaRevision> {
    const { http, databaseId } = await this.ensureClient();
    const params = new URLSearchParams();
    const tables = options?.tables;
    const tableList = Array.isArray(tables)
      ? tables
      : typeof tables === 'string'
        ? tables.split(',')
        : [];
    const normalizedTables = tableList.map((t) => t.trim()).filter(Boolean);
    if (normalizedTables.length) {
      params.append('tables', normalizedTables.map(encodeURIComponent).join(','));
    }
    const path = `/schemas/${encodeURIComponent(databaseId)}${
      params.size ? `?${params.toString()}` : ''
    }`;
    const res = await http.request<SchemaRevisionPayloadWithEntityText>('GET', path);
    return normalizeSchemaRevision(res, databaseId);
  }

  async getSchemaHistory(): Promise<SchemaHistoryEntry[]> {
    const { http, databaseId } = await this.ensureClient();
    const path = `/schemas/history/${encodeURIComponent(databaseId)}`;
    const res = await http.request<SchemaRevisionPayloadWithEntityText[]>('GET', path);
    return Array.isArray(res) ? res.map((entry) => normalizeSchemaRevision(entry, databaseId)) : [];
  }

  async diffSchema(localSchema: SchemaUpsertRequest): Promise<SchemaDiff> {
    const apiSchema = await this.getSchema();
    return computeSchemaDiff(apiSchema, localSchema);
  }

  async updateSchema(
    schema: SchemaUpsertRequest,
    options?: { publish?: boolean },
  ): Promise<SchemaRevision> {
    const { http, databaseId } = await this.ensureClient();
    const params = new URLSearchParams();
    if (options?.publish) params.append('publish', 'true');
    const path = `/schemas/${encodeURIComponent(databaseId)}${
      params.size ? `?${params.toString()}` : ''
    }`;
    const body = stripEntityText({ ...schema, databaseId: schema.databaseId ?? databaseId });
    const res = await http.request<SchemaRevisionPayloadWithEntityText>(
      'PUT',
      path,
      serializeDates(body),
    );
    return normalizeSchemaRevision(res, databaseId);
  }

  async validateSchema(schema: SchemaUpsertRequest): Promise<SchemaValidationResult> {
    const { http, databaseId } = await this.ensureClient();
    const path = `/schemas/${encodeURIComponent(databaseId)}/validate`;
    const body = stripEntityText({ ...schema, databaseId: schema.databaseId ?? databaseId });
    const res = await http.request<SchemaValidationPayloadWithEntityText>(
      'POST',
      path,
      serializeDates(body),
    );
    const normalizedSchema = res.schema
      ? normalizeSchemaRevision(res.schema, databaseId)
      : undefined;
    return {
      ...res,
      valid: res.valid ?? true,
      schema: normalizedSchema,
    };
  }

  async listSecrets(): Promise<SecretsListResponse> {
    const { http, databaseId } = await this.ensureClient();
    const path = `/database/${encodeURIComponent(databaseId)}/secret`;
    const response = await http.request<SecretsListResponse & { records: SecretMetadataPayload[] }>(
      'GET',
      path,
      undefined,
      { 'Content-Type': 'application/json' },
    );
    const records = (response.records ?? []).map(normalizeSecretMetadata);
    return {
      ...response,
      records,
      meta: response.meta ?? { totalRecords: records.length },
    };
  }

  async getSecret(key: string): Promise<SecretRecord> {
    const { http, databaseId } = await this.ensureClient();
    const path = `/database/${encodeURIComponent(databaseId)}/secret/${encodeURIComponent(key)}`;
    const record = await http.request<SecretRecordPayload>('GET', path, undefined, {
      'Content-Type': 'application/json',
    });
    return normalizeSecretRecord(record);
  }

  async putSecret(key: string, input: SecretSaveRequest): Promise<SecretMetadata> {
    const { http, databaseId } = await this.ensureClient();
    const path = `/database/${encodeURIComponent(databaseId)}/secret/${encodeURIComponent(key)}`;
    const response = await http.request<SecretMetadataPayload>('PUT', path, serializeDates(input));
    return normalizeSecretMetadata(response);
  }

  async deleteSecret(key: string): Promise<{ key: string }> {
    const { http, databaseId } = await this.ensureClient();
    const path = `/database/${encodeURIComponent(databaseId)}/secret/${encodeURIComponent(key)}`;
    const response = await http.request<{ key?: string } | { success?: boolean } | undefined>(
      'DELETE',
      path,
    );
    const deletedKey =
      response && typeof response === 'object' && 'key' in response
        ? (response as { key?: string }).key
        : undefined;
    return { key: deletedKey ?? key };
  }

  close(): void {
    for (const h of Array.from(this.streams)) {
      try {
        h.cancel();
      } catch {
        /* ignore */
      } finally {
        this.streams.delete(h);
      }
    }
  }

  /** -------- internal helpers used by builders -------- */

  async _count(table: string, select: SelectQuery, partition?: string): Promise<number> {
    const { http, databaseId } = await this.ensureClient();
    const params = new URLSearchParams();
    const p = table === 'ALL' ? undefined : partition ?? this.defaultPartition;
    if (p) params.append('partition', p);
    const path = `/data/${encodeURIComponent(databaseId)}/query/count/${encodeURIComponent(
      table,
    )}${params.toString() ? `?${params.toString()}` : ''}`;
    return http.requestEntity<number>('PUT', path, serializeDates(select));
  }

  async _queryPage<T>(
    table: string,
    select: SelectQuery,
    opts: { pageSize?: number; nextPage?: string; partition?: string } = {},
  ): Promise<QueryPage<T>> {
    const { http, databaseId } = await this.ensureClient();
    const params = new URLSearchParams();
    if (opts.pageSize != null) params.append('pageSize', String(opts.pageSize));
    if (opts.nextPage) params.append('nextPage', opts.nextPage);
    const p = table === 'ALL' ? undefined : opts.partition ?? this.defaultPartition;
    if (p) params.append('partition', p);
    const path = `/data/${encodeURIComponent(databaseId)}/query/${encodeURIComponent(
      table,
    )}${params.toString() ? `?${params.toString()}` : ''}`;
    return http.requestEntity<QueryPage<T>>('PUT', path, serializeDates(select));
  }

  async _update(table: string, update: UpdateQuery, partition?: string): Promise<unknown> {
    const { http, databaseId } = await this.ensureClient();
    const params = new URLSearchParams();
    const p = table === 'ALL' ? undefined : partition ?? this.defaultPartition;
    if (p) params.append('partition', p);
    const path = `/data/${encodeURIComponent(databaseId)}/query/update/${encodeURIComponent(
      table,
    )}${params.toString() ? `?${params.toString()}` : ''}`;
    return http.requestEntity('PUT', path, serializeDates(update));
  }

  async _deleteByQuery(table: string, select: SelectQuery, partition?: string): Promise<number> {
    const { http, databaseId } = await this.ensureClient();
    const params = new URLSearchParams();
    const p = table === 'ALL' ? undefined : partition ?? this.defaultPartition;
    if (p) params.append('partition', p);
    const path = `/data/${encodeURIComponent(databaseId)}/query/delete/${encodeURIComponent(
      table,
    )}${params.toString() ? `?${params.toString()}` : ''}`;
    return http.requestEntity<number>('PUT', path, serializeDates(select));
  }

  async _stream<T>(
    table: string,
    select: SelectQuery,
    includeQueryResults: boolean,
    keepAlive: boolean,
    handlers: {
      onItemAdded?: (e: T) => void;
      onItemUpdated?: (e: T) => void;
      onItemDeleted?: (e: T) => void;
      onItem?: (e: T | null, a: StreamAction) => void;
    },
  ): Promise<{ cancel: () => void }> {
    const { http, baseUrl, databaseId, fetchImpl, wireFormat } = await this.ensureClient();
    const params = new URLSearchParams();
    if (includeQueryResults) params.append('includeQueryResults', 'true');
    if (keepAlive) params.append('keepAlive', 'true');

    const url = `${baseUrl}/data/${encodeURIComponent(databaseId)}/query/stream/${encodeURIComponent(
      table,
    )}${params.toString() ? `?${params.toString()}` : ''}`;
    const serializedSelect = serializeDates(select);
    const handle =
      wireFormat === 'msgpack'
        ? await openMessagePackStream<T>(
            fetchImpl,
            url,
            {
              method: 'PUT',
              headers: http.headers({
                Accept: MESSAGEPACK_ACCEPT,
                'Content-Type': MESSAGEPACK_MEDIA_TYPE,
              }),
              body: encodeMessagePack(serializedSelect),
            },
            handlers,
          )
        : await openJsonLinesStream<T>(
            fetchImpl,
            url,
            {
              method: 'PUT',
              headers: http.headers({
                Accept: 'application/x-ndjson',
                'Content-Type': 'application/json',
              }),
              body: JSON.stringify(serializedSelect),
            },
            handlers,
          );
    return this.registerStream(handle);
  }

  async _saveInternal(
    table: string,
    entityOrEntities: unknown,
    options?: { relationships?: string[] },
  ): Promise<unknown> {
    const { http, databaseId } = await this.ensureClient();
    const params = new URLSearchParams();
    if (options?.relationships?.length) {
      params.append('relationships', options.relationships.map(encodeURIComponent).join(','));
    }
    const path = `/data/${encodeURIComponent(databaseId)}/${encodeURIComponent(table)}${
      params.toString() ? `?${params.toString()}` : ''
    }`;
    return http.requestEntity('PUT', path, serializeDates(entityOrEntities));
  }
}

/** -------------------------
 * QueryBuilder Implementation
 * --------------------------*/
class QueryBuilderImpl<T = unknown, S = Record<string, unknown>> implements IQueryBuilder<T> {
  private readonly db: OnyxDatabaseImpl<S>;
  private tableName: string | null;

  private fields: string[] | null = null;
  private resolvers: string[] | null = null;
  private conditions: QueryCondition | null = null;
  private sort: Sort[] | null = null;
  private limitValue: number | null = null;
  private distinctValue = false;
  private groupByValues: string[] | null = null;
  private partitionValue: string | undefined;

  private pageSizeValue: number | null = null;
  private nextPageValue: string | null = null;

  private mode: 'select' | 'update' = 'select';
  private updates: Partial<T> | null = null;

  private onItemAddedListener: ((e: T) => void) | null = null;
  private onItemUpdatedListener: ((e: T) => void) | null = null;
  private onItemDeletedListener: ((e: T) => void) | null = null;
  private onItemListener: ((e: T | null, a: StreamAction) => void) | null = null;

  constructor(db: OnyxDatabaseImpl<S>, table: string | null, partition?: string) {
    this.db = db;
    this.tableName = table;
    this.partitionValue = partition;
  }

  private ensureTable(): string {
    if (!this.tableName) throw new Error('Table is not defined. Call from(<table>) first.');
    return this.tableName;
  }

  private serializableConditions(): QueryCondition | null {
    return normalizeCondition(this.conditions);
  }

  private toSelectQuery(): SelectQuery {
    return {
      type: 'SelectQuery',
      table: this.tableName,
      fields: this.fields,
      conditions: this.serializableConditions(),
      sort: this.sort,
      limit: this.limitValue,
      distinct: this.distinctValue,
      groupBy: this.groupByValues,
      partition: this.partitionValue ?? null,
      resolvers: this.resolvers,
    };
  }

  private toUpdateQuery(): UpdateQuery {
    assertCandidateConditionIsReadOnly(this.conditions);
    return {
      type: 'UpdateQuery',
      conditions: this.serializableConditions(),
      updates: this.updates ?? {},
      sort: this.sort,
      limit: this.limitValue,
      partition: this.partitionValue ?? null,
    };
  }

  private async getAllRecordsForFormatting(): Promise<T[]> {
    if (this.mode !== 'select') throw new Error('Formatting is only applicable in select mode.');
    const table = this.ensureTable();
    const select = this.toSelectQuery();
    const pageSize = this.pageSizeValue ?? undefined;
    const initialNextPage = this.nextPageValue ?? undefined;
    return collectAllQueryRecords<T>((nextPage?: string) => (
      this.db._queryPage<T>(table, select, {
        pageSize,
        nextPage,
        partition: this.partitionValue,
      })
    ), initialNextPage);
  }

  private toSerializableQueryObject(): (SelectQuery | UpdateQuery) & { table: string } {
    const table = this.ensureTable();
    const payload = this.mode === 'update' ? this.toUpdateQuery() : this.toSelectQuery();
    return { ...payload, table };
  }

  from(table: string): IQueryBuilder<T> {
    this.tableName = table;
    return this;
  }

  select(...fields: Array<string | string[]>): IQueryBuilder<T> {
    const flat = flattenStrings(fields);
    this.fields = flat.length > 0 ? flat : null;
    return this;
  }

  resolve(...values: Array<string | string[]>): IQueryBuilder<T> {
    const flat = flattenStrings(values);
    this.resolvers = flat.length > 0 ? flat : null;
    return this;
  }

  search(queryText: string, minScore?: number | null): IQueryBuilder<T>;
  search(queryText: string, options: SearchOptions): IQueryBuilder<T>;
  search(searchQuery: VectorSearchQueryInput): IQueryBuilder<T>;
  search(
    queryTextOrSearch: string | VectorSearchQueryInput,
    minScoreOrOptions?: number | null | SearchOptions,
  ): IQueryBuilder<T> {
    if (
      typeof queryTextOrSearch === 'string' &&
      typeof minScoreOrOptions === 'object' &&
      minScoreOrOptions !== null
    ) {
      return this.and({
        field: '__full_text__',
        operator: 'SEARCH',
        value: searchCriteriaValue(queryTextOrSearch, minScoreOrOptions),
      });
    }
    const value = typeof queryTextOrSearch === 'string'
      ? { queryText: queryTextOrSearch, minScore: minScoreOrOptions ?? null }
      : vectorSearchQuery(queryTextOrSearch);
    return this.and({
      field: '__full_text__',
      operator: 'MATCHES',
      value,
    });
  }

  approximateSearch(searchQuery: VectorSearchQueryInput): IQueryBuilder<T>;
  approximateSearch(queryText: string, options?: ApproximateSearchOptions): IQueryBuilder<T>;
  approximateSearch(
    queryTextOrSearch: string | VectorSearchQueryInput,
    options: ApproximateSearchOptions = {},
  ): IQueryBuilder<T> {
    if (this.conditions !== null) throw new Error('SEARCH_CANDIDATES must be the sole root criterion');
    const searchQuery = vectorSearchQuery(
      typeof queryTextOrSearch === 'string'
        ? { text: queryTextOrSearch, ...options }
        : queryTextOrSearch,
    );
    if (searchQuery.text === null || searchQuery.semantic !== null) {
      throw new Error('SEARCH_CANDIDATES supports text-only VectorSearchQuery values');
    }
    this.conditions = toSingleCondition({
      field: '__full_text__',
      operator: 'SEARCH_CANDIDATES',
      value: searchQuery,
    });
    return this;
  }

  hnswCandidates(searchQuery: HnswSearchQueryInput): IQueryBuilder<T> {
    if (this.conditions !== null) throw new Error('HNSW_CANDIDATES must be the sole root criterion');
    this.conditions = toSingleCondition({
      field: '__full_text__',
      operator: 'HNSW_CANDIDATES',
      value: hnswSearchQuery(searchQuery),
    });
    return this;
  }

  /** @deprecated Prefer `where(approximateCandidates(...))`. */
  approximateCandidates(
    attribute: string,
    valueOrValues: unknown | readonly unknown[],
    maxCandidates?: number,
  ): IQueryBuilder<T> {
    if (attribute.trim().length === 0) throw new TypeError('candidate attribute must not be blank');
    return this.and({
      field: attribute,
      operator: 'CANDIDATES',
      value: approximateIndexCandidateQuery(valueOrValues, maxCandidates),
    });
  }

  where(condition: ConditionInput): IQueryBuilder<T> {
    const c = toCondition(condition);
    assertCandidateConditionIsComposable(this.conditions, c, 'AND');
    assertSearchConditionIsComposable(this.conditions, c);
    if (!this.conditions) {
      this.conditions = c;
    } else {
      this.conditions = {
        conditionType: 'CompoundCondition',
        operator: 'AND',
        conditions: [this.conditions, c],
      };
    }
    return this;
  }

  and(condition: ConditionInput): IQueryBuilder<T> {
    const c = toCondition(condition);
    assertCandidateConditionIsComposable(this.conditions, c, 'AND');
    assertSearchConditionIsComposable(this.conditions, c);
    if (!this.conditions) {
      this.conditions = c;
    } else if (
      this.conditions.conditionType === 'CompoundCondition' &&
      this.conditions.operator === 'AND'
    ) {
      this.conditions.conditions.push(c);
    } else {
      this.conditions = {
        conditionType: 'CompoundCondition',
        operator: 'AND',
        conditions: [this.conditions, c],
      };
    }
    return this;
  }

  or(condition: ConditionInput): IQueryBuilder<T> {
    const c = toCondition(condition);
    assertCandidateConditionIsComposable(this.conditions, c, 'OR');
    assertSearchConditionIsComposable(this.conditions, c);
    if (!this.conditions) {
      this.conditions = c;
    } else if (
      this.conditions.conditionType === 'CompoundCondition' &&
      this.conditions.operator === 'OR'
    ) {
      this.conditions.conditions.push(c);
    } else {
      this.conditions = {
        conditionType: 'CompoundCondition',
        operator: 'OR',
        conditions: [this.conditions, c],
      };
    }
    return this;
  }

  orderBy(...sorts: Sort[]): IQueryBuilder<T> {
    this.sort = sorts;
    return this;
  }

  groupBy(...fields: string[]): IQueryBuilder<T> {
    this.groupByValues = fields.length ? fields : null;
    return this;
  }

  distinct(): IQueryBuilder<T> {
    this.distinctValue = true;
    return this;
  }

  limit(n: number): IQueryBuilder<T> {
    this.limitValue = n;
    return this;
  }

  inPartition(partition: string): IQueryBuilder<T> {
    this.partitionValue = partition;
    return this;
  }

  pageSize(n: number): IQueryBuilder<T> {
    this.pageSizeValue = n;
    return this;
  }

  nextPage(token: string): IQueryBuilder<T> {
    this.nextPageValue = token;
    return this;
  }

  setUpdates(updates: Partial<T>): IQueryBuilder<T> {
    assertCandidateConditionIsReadOnly(this.conditions);
    this.mode = 'update';
    this.updates = updates;
    return this;
  }

  async count(): Promise<number> {
    if (this.mode !== 'select') throw new Error('Cannot call count() in update mode.');
    const table = this.ensureTable();
    return this.db._count(table, this.toSelectQuery(), this.partitionValue);
  }

  async page(
    options: { pageSize?: number; nextPage?: string } = {},
  ): Promise<{ records: T[]; nextPage?: string | null }> {
    if (this.mode !== 'select') throw new Error('Cannot call page() in update mode.');
    const table = this.ensureTable();
    const final = {
      pageSize: this.pageSizeValue ?? options.pageSize,
      nextPage: this.nextPageValue ?? options.nextPage,
      partition: this.partitionValue,
    };
    return this.db._queryPage<T>(table, this.toSelectQuery(), final);
  }

  list(options: {
    pageSize?: number;
    nextPage?: string;
  } = {}): QueryResultsPromise<T> {
    const size = this.pageSizeValue ?? options.pageSize;
    const pgPromise = this.page(options).then(pg => {
      const fetcher = (token: string) => this.nextPage(token).list({ pageSize: size });
      return new QueryResults<T>(Array.isArray(pg.records) ? pg.records : [], pg.nextPage ?? null, fetcher);
    });
    for (const m of Object.getOwnPropertyNames(QueryResults.prototype) as string[]) {
      if (m === 'constructor') continue;
      (pgPromise as any)[m] = (...args: any[]) => pgPromise.then(res => (res as any)[m](...args));
    }
    return pgPromise as QueryResultsPromise<T>;
  }

  async firstOrNull(): Promise<T | null> {
    if (this.mode !== 'select') throw new Error('Cannot call firstOrNull() in update mode.');
    this.limitValue = 1;
    const pg = await this.page();
    return Array.isArray(pg.records) && pg.records.length > 0 ? pg.records[0] : null;
  }

  async one(): Promise<T | null> {
    return this.firstOrNull();
  }

  async table(options?: TableFormatOptions): Promise<string> {
    return formatQueryResultsAsTable(
      await this.getAllRecordsForFormatting(),
      options,
      this.fields ?? undefined,
    );
  }

  async tree(options?: TreeFormatOptions): Promise<string> {
    return formatQueryResultsAsTree(await this.getAllRecordsForFormatting(), options);
  }

  async csv(options?: CsvFormatOptions): Promise<string> {
    return formatQueryResultsAsCsv(
      await this.getAllRecordsForFormatting(),
      options,
      this.fields ?? undefined,
    );
  }

  async json(options?: JsonFormatOptions): Promise<string> {
    return formatQueryResultsAsJson(await this.getAllRecordsForFormatting(), options);
  }

  async delete(): Promise<number> {
    if (this.mode !== 'select') throw new Error('delete() is only applicable in select mode.');
    assertCandidateConditionIsReadOnly(this.conditions);
    const table = this.ensureTable();
    return this.db._deleteByQuery(table, this.toSelectQuery(), this.partitionValue);
  }

  async update(): Promise<unknown> {
    if (this.mode !== 'update') throw new Error('Call setUpdates(...) before update().');
    const table = this.ensureTable();
    const update = this.toUpdateQuery();
    return this.db._update(table, update, this.partitionValue);
  }

  onItemAdded(listener: (entity: T) => void): IQueryBuilder<T> {
    this.onItemAddedListener = listener;
    return this;
  }

  onItemUpdated(listener: (entity: T) => void): IQueryBuilder<T> {
    this.onItemUpdatedListener = listener;
    return this;
  }

  onItemDeleted(listener: (entity: T) => void): IQueryBuilder<T> {
    this.onItemDeletedListener = listener;
    return this;
  }

  onItem(listener: (entity: T | null, action: StreamAction) => void): IQueryBuilder<T> {
    this.onItemListener = listener;
    return this;
  }

  async streamEventsOnly(keepAlive = true): Promise<{ cancel: () => void }> {
    return this.stream(false, keepAlive);
  }

  async streamWithQueryResults(keepAlive = false): Promise<{ cancel: () => void }> {
    return this.stream(true, keepAlive);
  }

  async stream(includeQueryResults = true, keepAlive = false): Promise<{ cancel: () => void }> {
    if (this.mode !== 'select') throw new Error('Streaming is only applicable in select mode.');
    assertSearchConditionSupportsStreaming(this.conditions);
    const table = this.ensureTable();
    return this.db._stream<T>(table, this.toSelectQuery(), includeQueryResults, keepAlive, {
      onItemAdded: this.onItemAddedListener ?? undefined,
      onItemUpdated: this.onItemUpdatedListener ?? undefined,
      onItemDeleted: this.onItemDeletedListener ?? undefined,
      onItem: this.onItemListener ?? undefined,
    });
  }
}

/** -------------------------
 * SaveBuilder Implementation
 * --------------------------*/
class SaveBuilderImpl<T = unknown, S = Record<string, unknown>> implements ISaveBuilder<T> {
  private readonly db: OnyxDatabaseImpl<S>;
  private readonly table: string;
  private relationships: string[] | null = null;

  constructor(db: OnyxDatabaseImpl<S>, table: string) {
    this.db = db;
    this.table = table;
  }

  cascade(...relationships: Array<string | string[]>): ISaveBuilder<T> {
    this.relationships = relationships.flat();
    return this;
  }

  one(entity: Partial<T>): Promise<unknown> {
    const opts = this.relationships ? { relationships: this.relationships } : undefined;
    return this.db._saveInternal(this.table, entity, opts);
  }

  many(entities: Array<Partial<T>>): Promise<unknown> {
    const opts = this.relationships ? { relationships: this.relationships } : undefined;
    return this.db._saveInternal(this.table, entities, opts);
  }
}

/** -------------------------
 * CascadeBuilder Implementation
 * --------------------------*/
class CascadeBuilderImpl<Schema = Record<string, unknown>>
  implements ICascadeBuilder<Schema>
{
  private readonly db: OnyxDatabaseImpl<Schema>;
  private rels: string[] | null = null;

  constructor(db: OnyxDatabaseImpl<Schema>) {
    this.db = db;
  }

  cascade(...relationships: Array<string | string[]>): ICascadeBuilder<Schema> {
    this.rels = relationships.flat();
    return this;
  }

  save<Table extends keyof Schema & string>(
    table: Table,
    entityOrEntities: Partial<Schema[Table]> | Array<Partial<Schema[Table]>>,
  ): Promise<unknown> {
    const opts = this.rels ? { relationships: this.rels } : undefined;
    return this.db._saveInternal(String(table), entityOrEntities, opts);
  }

  delete<Table extends keyof Schema & string>(
    table: Table,
    primaryKey: string,
  ): Promise<boolean> {
    const opts = this.rels ? { relationships: this.rels } : undefined;
    return this.db.delete(table, primaryKey, opts);
  }
}

type ReadableStreamReader = {
  read(): Promise<{ done: boolean; value?: Uint8Array }>;
  cancel?: (reason?: unknown) => void;
};

type ReadableStreamLike = {
  getReader(): ReadableStreamReader;
};

class AiChatClientImpl implements AiChatClient {
  constructor(
    private readonly resolveAiClient: () => Promise<{
      http: HttpClient;
      fetchImpl: FetchImpl;
      aiBaseUrl: string;
      databaseId: string;
      defaultModel: string;
    }>,
    private readonly registerStream: (handle: { cancel: () => void }) => { cancel: () => void },
    private readonly requestLoggingEnabled: () => boolean,
    private readonly responseLoggingEnabled: () => boolean,
  ) {}

  private normalizeEventData(rawEvent: string): string | null {
    const lines = rawEvent.split('\n');
    const dataLines: string[] = [];
    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed || trimmed.startsWith(':')) continue;
      if (trimmed.startsWith('data:')) {
        dataLines.push(trimmed.slice(5).trim());
      } else {
        dataLines.push(trimmed);
      }
    }
    if (!dataLines.length) return null;
    const joined = dataLines.join('\n').trim();
    return joined.length > 0 ? joined : null;
  }

  private logRequest(url: string, body: unknown, headers: Record<string, string>): void {
    if (!this.requestLoggingEnabled()) return;
    console.log(`POST ${url}`);
    if (body != null) {
      const serialized = typeof body === 'string' ? body : JSON.stringify(body);
      console.log(serialized);
    }
    const headerLog = { ...headers };
    if (headerLog['x-onyx-secret']) headerLog['x-onyx-secret'] = '[REDACTED]';
    console.log('Headers:', headerLog);
  }

  private logResponse(status: number, statusText: string, raw?: string): void {
    if (!this.responseLoggingEnabled()) return;
    const statusLine = `${status} ${statusText}`.trim();
    console.log(statusLine);
    if (raw && raw.trim().length > 0) {
      console.log(raw);
    }
  }

  private toOnyxError(
    status: number,
    statusText: string,
    raw: string,
  ): OnyxHttpError {
    let parsed: unknown = raw;
    try {
      parsed = parseJsonAllowNaN(raw);
    } catch {
      /* ignore */
    }
    const message =
      typeof parsed === 'object' &&
      parsed !== null &&
      'error' in parsed &&
      typeof (parsed as { error?: { message?: unknown } }).error === 'object' &&
      typeof (parsed as { error?: { message?: unknown } }).error?.message === 'string'
        ? String((parsed as { error: { message: unknown } }).error.message)
        : `${status} ${statusText}`;
    return new OnyxHttpError(message, status, statusText, parsed, raw);
  }

  create(
    request: AiChatCompletionRequest & { stream?: false },
    options?: AiRequestOptions,
  ): Promise<AiChatCompletionResponse>;
  create(
    request: AiChatCompletionRequest & { stream: true },
    options?: AiRequestOptions,
  ): Promise<AiChatCompletionStream>;
  async create(
    request: AiChatCompletionRequest,
    options?: AiRequestOptions,
  ): Promise<AiChatCompletionResponse | AiChatCompletionStream> {
    const body = { ...request, stream: !!request.stream };
    const { http, fetchImpl, aiBaseUrl, databaseId } = await this.resolveAiClient();
    const params = new URLSearchParams();
    const scopedDb = options?.databaseId ?? databaseId;
    if (scopedDb) params.append('databaseId', scopedDb);
    const path = `/v1/chat/completions${params.size ? `?${params.toString()}` : ''}`;

    if (!body.stream) {
      return http.request<AiChatCompletionResponse>('POST', path, body);
    }

    const url = `${aiBaseUrl}${path}`;
    const headers = http.headers({ Accept: 'text/event-stream' });
    this.logRequest(url, body, headers);
    const res = await fetchImpl(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });

    if (!res.ok) {
      const raw = await res.text();
      this.logResponse(res.status, res.statusText, raw);
      throw this.toOnyxError(res.status, res.statusText, raw);
    }
    this.logResponse(res.status, res.statusText);

    const bodyStream = (res as { body?: unknown }).body;
    const reader =
      bodyStream && typeof (bodyStream as { getReader?: unknown }).getReader === 'function'
        ? (bodyStream as ReadableStreamLike).getReader()
        : null;
    if (!reader) {
      throw new OnyxHttpError(
        'Streaming response body is not readable',
        res.status,
        res.statusText,
        null,
        '',
      );
    }

    let canceled = false;
    const handle = {
      cancel: () => {
        if (canceled) return;
        canceled = true;
        try {
          reader.cancel?.();
        } catch {
          /* ignore */
        }
      },
    };
    const registered = this.registerStream(handle);
    const normalizeEvent = (rawEvent: string): string | null => this.normalizeEventData(rawEvent);

    const stream: AiChatCompletionStream = {
      async *[Symbol.asyncIterator](): AsyncIterator<AiChatCompletionChunk> {
        const decoder = new TextDecoder('utf-8');
        let buffer = '';
        try {
          while (!canceled) {
            const { done, value } = await reader.read();
            if (done) break;
            if (value) {
              buffer += decoder.decode(value, { stream: true });
            }
            let splitIndex = buffer.indexOf('\n\n');
            while (splitIndex !== -1) {
              const rawEvent = buffer.slice(0, splitIndex);
              buffer = buffer.slice(splitIndex + 2);
              const data = normalizeEvent(rawEvent);
              if (data === '[DONE]') return;
              if (data) {
                try {
                  yield parseJsonAllowNaN(data) as AiChatCompletionChunk;
                } catch {
                  /* ignore parse errors */
                }
              }
              splitIndex = buffer.indexOf('\n\n');
            }
          }
          buffer += decoder.decode();
          const remaining = buffer.trim();
          if (remaining && remaining !== '[DONE]') {
            const data = normalizeEvent(remaining);
            if (data && data !== '[DONE]') {
              try {
                yield parseJsonAllowNaN(data) as AiChatCompletionChunk;
              } catch {
                /* ignore */
              }
            }
          }
        } finally {
          registered.cancel();
        }
      },
      cancel() {
        registered.cancel();
      },
    };

    return stream;
  }
}

/** -------------------------
 * Facade export
 * --------------------------*/
export function createOnyxFacade(resolveConfig: ResolveConfig): OnyxFacade {
  const cachedCfgs = new Map<string, { promise: Promise<ResolvedConfig>; expires: number }>();

  const cacheKey = (
    databaseId?: string,
    apiKey?: string,
    wireFormat: OnyxConfig['wireFormat'] = 'msgpack',
  ): string | null => {
    const id = typeof databaseId === 'string' && databaseId.trim() !== '' ? databaseId.trim() : null;
    const key = typeof apiKey === 'string' && apiKey.trim() !== '' ? apiKey.trim() : null;
    return id && key ? `${id}-${key}-${wireFormat ?? 'msgpack'}` : null;
  };

  function resolveConfigWithCache(config?: OnyxConfig): Promise<ResolvedConfig> {
    const ttl = config?.ttl ?? DEFAULT_CACHE_TTL;
    const now = Date.now();
    const hintKey =
      cacheKey(config?.databaseId, config?.apiKey, config?.wireFormat) ??
      `__default__-${config?.wireFormat ?? 'msgpack'}`;

    const existing = cachedCfgs.get(hintKey);
    if (existing && existing.expires > now) {
      return existing.promise;
    }
    if (existing) cachedCfgs.delete(hintKey);

    const { ttl: _ttl, requestLoggingEnabled: _reqLog, responseLoggingEnabled: _resLog, ...rest } = config ?? {};
    void _ttl;
    void _reqLog;
    void _resLog;
    const expires = now + ttl;

    const promise = resolveConfig(rest).then((resolved) => {
      const resolvedKey =
        cacheKey(resolved.databaseId, resolved.apiKey, resolved.wireFormat) ?? hintKey;
      const nextExpires = Date.now() + ttl;
      cachedCfgs.set(resolvedKey, { promise, expires: nextExpires });
      if (resolvedKey !== hintKey) {
        const stale = cachedCfgs.get(hintKey);
        if (stale && stale.promise === promise) cachedCfgs.delete(hintKey);
      }
      return resolved;
    });

    cachedCfgs.set(hintKey, { promise, expires });
    return promise;
  }

  function clearCacheConfig(): void {
    cachedCfgs.clear();
  }

  return {
    init<Schema = Record<string, unknown>>(config?: OnyxConfig): IOnyxDatabase<Schema> {
      return new OnyxDatabaseImpl<Schema>(config, resolveConfigWithCache);
    },
    clearCacheConfig,
  };
}
